import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Editor, { type OnMount } from '@monaco-editor/react'
import { QPARAM_THEME, registerQParam } from './qparamMonaco'
import Prose from './Prose'
import { compileFront, declaredParams, decodeOcamlEscapes, loadManifest, runVm, type BrowserManifest } from './runtime'
import {
  formalrvExplorerApi,
  type FRXExample,
  type FRXStep,
  type FRXVariant,
  type FRXVerdict,
} from '../../utils/api'

type IEditor = Parameters<OnMount>[0]
type MonacoNS = Parameters<OnMount>[1]

const cache = new Map<string, Promise<FRXExample>>()
export function loadExample(id: string): Promise<FRXExample> {
  if (!cache.has(id)) cache.set(id, formalrvExplorerApi.getExample(id).catch(err => { cache.delete(id); throw err }))
  return cache.get(id)!
}

type PaneId = 'qppm' | 'qec' | 'sysprog' | 'passes' | 'sysspec' | 'machine' | 'diff'

const PANE_LABEL: Record<PaneId, string> = {
  qppm: '.qppm', qec: '.qec', sysprog: '.sysprog', passes: 'Passes', sysspec: 'Contracts', machine: 'Machine', diff: 'Diff',
}

const STAGE_TOOLS: { from: string; to: string; tool: string }[] = [
  { from: 'prog', to: 'qppm', tool: 'prepare_qppm' },
  { from: 'qppm', to: 'qec', tool: 'prepare_qec' },
  { from: 'qec', to: 'sysprog', tool: 'qec_sys_staged' },
  { from: 'sysprog', to: 'vm', tool: 'resource_vm' },
]

type Step = FRXStep & { where?: 'browser' }

interface View {
  texts: Partial<Record<'prog' | 'qppm' | 'qec' | 'sysprog' | 'sysspec', string>>
  steps: (Step | null)[]  // aligned with STAGE_TOOLS; null = not run
  passes: { name: string; text: string }[]
  vm: FRXVerdict | null
  machine: string
  machinePath: string
  diff?: string
  qppmSupplied: boolean
  liveNote?: string
}

/** The fields the explorer shows, from a raw `resource_vm --json` verdict. */
function verdictOf(v: Record<string, unknown>): FRXVerdict {
  const loc = (v.location && typeof v.location === 'object' ? v.location : {}) as Record<string, unknown>
  const ops = Object.fromEntries(Object.entries((loc.opcode_counts ?? {}) as Record<string, number>).filter(([, n]) => n))
  const findings = Object.fromEntries(Object.entries((v.protocol_findings ?? {}) as Record<string, { count?: number | null }>)
    .filter(([, f]) => f && typeof f === 'object' && 'count' in f).map(([k, f]) => [k, f.count ?? null]))
  return {
    status: String(v.status), code: (v.code as string) ?? null, message: (v.message as string) ?? null,
    line: (v.line as number) ?? null, mode: (v.mode as string) ?? null,
    full_resource_validation: (v.full_resource_validation as boolean) ?? null,
    peak_qubits: (v.peak_qubits_upper_bound as number) ?? null, initial_data_qubits: (v.initial_data_qubits as number) ?? null,
    runtime_ns: (v.runtime_upper_bound_ns as number) ?? null,
    machine: (v.machine && typeof v.machine === 'object' ? v.machine : {}) as FRXVerdict['machine'],
    services: (v.service_reservations ?? {}) as Record<string, number>, opcode_counts: ops,
    dynamic_instruction_count: (v.dynamic_instruction_count as number) ?? null, findings, scope: (v.scope as string) ?? null,
  }
}

/** Combine the main run with a variant: a variant re-runs the chain from the .qppm on. */
function viewOf(ex: FRXExample, variant: FRXVariant | null): View {
  if (!variant) {
    return {
      texts: ex.texts, steps: STAGE_TOOLS.map(t => ex.steps.find(s => s.from === t.from) ?? null),
      passes: ex.passes, vm: ex.vm, machine: ex.machine, machinePath: ex.machine_path, qppmSupplied: false,
    }
  }
  const supplied = variant.kind === 'wrong-artifact'
  const first = supplied ? null : ex.steps.find(s => s.from === 'prog') ?? null
  return {
    texts: { prog: ex.texts.prog, qppm: supplied ? variant.qppm : ex.texts.qppm, ...variant.texts },
    steps: [first, ...STAGE_TOOLS.slice(1).map(t => variant.steps.find(s => s.from === t.from) ?? null)],
    passes: variant.passes,
    vm: variant.vm,
    machine: variant.machine ?? ex.machine,
    machinePath: variant.machine ? `FormalRV/Examples/${ex.unit}/${ex.unit}.machine` : ex.machine_path,
    diff: variant.diff,
    qppmSupplied: supplied,
  }
}

// ---------------------------------------------------------------------------
// Cross-pane identifier highlighting (godbolt-style "follow this name")
// ---------------------------------------------------------------------------

const HL_STYLE = `
.frx-word { background: rgba(42,120,214,0.18); border-radius: 2px; outline: 1px solid rgba(42,120,214,0.45); }
.frx-errline { background: rgba(225,29,72,0.10); }
.frx-errglyph { background: #e11d48; width: 4px !important; margin-left: 3px; border-radius: 2px; }
`

function useLinkedEditors() {
  const editors = useRef(new Map<string, { ed: IEditor; monaco: MonacoNS; deco?: { clear(): void } }>())
  const [word, setWord] = useState<string | null>(null)

  const apply = useCallback((w: string | null) => {
    for (const entry of editors.current.values()) {
      entry.deco?.clear()
      const model = entry.ed.getModel()
      if (!w || !model) continue
      const matches = model.findMatches(w, false, false, true, '`~!@#$%^&*()-=+[{]}\\|;:\'",.<>/?', false)
      entry.deco = entry.ed.createDecorationsCollection(matches.slice(0, 500).map(m => ({
        range: m.range, options: { inlineClassName: 'frx-word' },
      })))
    }
  }, [])

  useEffect(() => { apply(word) }, [word, apply])

  // One marked line per editor: where a refusal points in the source.
  const marks = useRef(new Map<string, { clear(): void }>())
  const markLine = useCallback((key: string, line: number | null) => {
    marks.current.get(key)?.clear()
    marks.current.delete(key)
    const entry = editors.current.get(key)
    if (!entry || line == null) return
    const range = new entry.monaco.Range(line, 1, line, 1)
    marks.current.set(key, entry.ed.createDecorationsCollection([{
      range, options: { isWholeLine: true, className: 'frx-errline', glyphMarginClassName: 'frx-errglyph' },
    }]))
    entry.ed.revealLineInCenterIfOutsideViewport(line)
  }, [])

  const register = useCallback((key: string): OnMount => (ed, monaco) => {
    editors.current.set(key, { ed, monaco })
    ed.onDidChangeCursorSelection(() => {
      const model = ed.getModel()
      const pos = ed.getPosition()
      if (!model || !pos) return
      const w = model.getWordAtPosition(pos)
      setWord(w && w.word.length > 1 ? w.word : null)
    })
    ed.onDidDispose(() => editors.current.delete(key))
    apply(word)
  }, [apply, word])

  return { word, setWord, register, markLine, refresh: () => apply(word) }
}

/** A Monaco editor that fills its box (the box sets the size). */
function CodePane({ value, onMount, language = 'qparam', readOnly = true, onChange }: {
  value: string; onMount: OnMount; language?: string; readOnly?: boolean; onChange?: (v: string) => void
}) {
  return (
    <div className="relative flex-1 min-h-0">
      <div className="absolute inset-0">
        <Editor
          height="100%"
          language={language}
          theme={QPARAM_THEME}
          value={value}
          beforeMount={registerQParam}
          onMount={onMount}
          onChange={v => onChange?.(v ?? '')}
          options={{
            readOnly, domReadOnly: readOnly, minimap: { enabled: false }, fontSize: 13, lineNumbers: 'on',
            wordWrap: 'on', scrollBeyondLastLine: false, automaticLayout: true, renderLineHighlight: 'none',
            folding: false, lineDecorationsWidth: 6, overviewRulerLanes: 0, contextmenu: false, glyphMargin: true,
          }}
        />
      </div>
    </div>
  )
}

const bar = 'h-9 shrink-0 px-2 flex items-center gap-1.5 border-b border-qcloud-border bg-slate-50/80'
const btn = 'h-7 px-2.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40'
const sel = 'h-7 pl-2 pr-6 rounded-md border border-qcloud-border bg-white text-xs text-qcloud-text'

// ---------------------------------------------------------------------------
// Verdict
// ---------------------------------------------------------------------------

function VerdictPanel({ view }: { view: View }) {
  const vm = view.vm
  if (!vm) {
    return <p className="text-sm text-qcloud-muted">{view.liveNote ? <Prose text={view.liveNote} /> : 'The resource VM has not run: there is no schedule to check.'}</p>
  }
  const ok = vm.status === 'RESOURCE_VALIDATED'
  const services = Object.entries(vm.services)
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
      <div className="space-y-1">
        <div className={`font-semibold ${ok ? 'text-emerald-700' : 'text-rose-700'}`}>{ok ? '✓' : '✗'} {vm.status}{vm.code ? ` · ${vm.code}` : ''}</div>
        {vm.message && <p className="font-mono text-xs text-qcloud-text max-w-2xl break-words">{vm.message}</p>}
        {ok && <div className="text-qcloud-text">
          peak <b>{vm.peak_qubits}</b> qubits{vm.initial_data_qubits != null && vm.peak_qubits != null && <span className="text-qcloud-muted"> ({vm.initial_data_qubits} data)</span>}
          {' · '}runtime <b>{vm.runtime_ns} ns</b>
        </div>}
      </div>
      {ok && Object.keys(vm.opcode_counts).length > 0 && (
        <div className="flex flex-wrap gap-1 content-start max-w-md">
          {Object.entries(vm.opcode_counts).map(([k, v]) => (
            <span key={k} className="px-1.5 py-0.5 rounded bg-slate-100 font-mono text-[11px] text-qcloud-text">{k} × {v}</span>
          ))}
        </div>
      )}
      {ok && services.length > 0 && (
        <table className="text-[11.5px]">
          <tbody>
            {services.map(([k, used]) => (
              <tr key={k}>
                <td className="pr-3 font-mono text-qcloud-muted">{k}</td>
                <td className="text-right font-mono text-qcloud-text tabular-nums">{used} / {vm.machine[k]?.value ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Explorer
// ---------------------------------------------------------------------------

export interface ExplorerProps {
  exampleId: string
  /** an example supplied by the caller instead of a recorded one (the playground's) */
  example?: FRXExample
  /** your own program: no recorded runs, the source kept by the caller */
  playground?: boolean
  onSourceChange?: (text: string) => void
  /** the code distance for in-browser compiles when the source declares none */
  distance?: number
  /** controls placed first in the source pane's toolbar (an example picker) */
  toolbar?: React.ReactNode
  /** called after each in-browser compile with its outcome (exercises check these) */
  onCompiled?: (result: { steps: (Step | null)[]; texts: { prog: string; qppm?: string; qec?: string } }) => void
  /** called after each in-browser VM check with the listing and the VM's raw verdict */
  onVmChecked?: (result: { sysprog: string; verdict: Record<string, unknown> | null }) => void
  /** hide the compile button (an exercise on a listing) */
  hideCompile?: boolean
  variantId?: string | null
  onVariantChange?: (id: string | null) => void
  initialPane?: PaneId
  showVariants?: boolean
}

/** The source line a refusal points at: the statement it quotes, or a name it puts in backticks. */
function errorLine(source: string, log: string): number | null {
  const lines = source.split('\n')
  const find = (needle: string) => {
    const n = needle.trim()
    if (n.length < 2) return null
    const i = lines.findIndex(l => l.includes(n))
    return i >= 0 ? i + 1 : null
  }
  // The reader quotes the refused statement joined with the lines after it: match its first segment.
  const stmt = log.match(/statement:\s*(.+?)\s*"?\)?\s*$/m)
  if (stmt) { const l = find(stmt[1].split(/\s{2,}/)[0]); if (l) return l }
  for (const m of log.matchAll(/`([^`]{2,40})`/g)) { const l = find(m[1]); if (l) return l }
  return null
}

const MAIN_PANES: PaneId[] = ['qppm', 'qec', 'sysprog']
const MORE_PANES: PaneId[] = ['passes', 'sysspec', 'machine']

/** The explorer fills its parent: give the parent a height (a flex child, or h-full). */
export default function Explorer({
  exampleId, example, playground = false, onSourceChange, distance, toolbar, onCompiled, onVmChecked, hideCompile = false,
  variantId = null, onVariantChange, initialPane = 'qppm', showVariants = true,
}: ExplorerProps) {
  const [ex, setEx] = useState<FRXExample | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [localVariant, setLocalVariant] = useState<string | null>(variantId)
  const [pane, setPane] = useState<PaneId>(initialPane)
  const [passIdx, setPassIdx] = useState(0)
  /** the bottom drawer: a step's log (its index) or the VM's verdict */
  const [drawer, setDrawer] = useState<number | 'vm' | null>(null)
  const linked = useLinkedEditors()

  // FormalRV's tools compiled to JavaScript, run in this browser on edited text.
  const [tools, setTools] = useState<BrowserManifest | null>(null)
  const [draft, setDraft] = useState<{ prog?: string; sysprog?: string }>({})
  const [live, setLive] = useState<Partial<View> | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [scalars, setScalars] = useState<Record<string, number>>({})
  useEffect(() => { loadManifest().then(setTools) }, [])
  const resetLive = () => { setLive(null); setDraft({}); linked.markLine('prog', null) }

  useEffect(() => { setLocalVariant(variantId) }, [variantId])
  useEffect(() => {
    setEx(null); setError(null); setDrawer(null); resetLive()
    if (example) { setEx(example); setScalars(example.specialize); return }
    loadExample(exampleId).then(e => { setEx(e); setScalars(e.specialize) }).catch(e => setError(e?.message || 'Failed to load'))
  }, [exampleId, example, playground])

  const variant = ex?.variants.find(v => v.id === localVariant) ?? null
  const base = useMemo(() => (ex ? viewOf(ex, variant) : null), [ex, variant])
  const view = useMemo<View | null>(() => (base && live ? { ...base, ...live, texts: { ...base.texts, ...live.texts } } : base), [base, live])
  const progText = draft.prog ?? view?.texts.prog ?? ''
  const sysText = draft.sysprog ?? view?.texts.sysprog ?? ''

  const compile = async () => {
    if (!ex) return
    setBusy('Compiling in your browser…')
    linked.markLine('prog', null)
    try {
      const specialize = Object.fromEntries(declaredParams(progText).map(p => [p, scalars[p] ?? 1]))
      const r = await compileFront(progText, {
        specialize, code: ex.code.family, distance: distance ?? ex.code.distance,
        qecOpts: ex.qec_opts, sourcePath: ex.source_path, includes: ex.includes,
      })
      const steps: (Step | null)[] = [null, null, null, null]
      r.steps.forEach((s, i) => { steps[i] = { ...s, where: 'browser' } })
      const refusedStep = steps.find(s => s && s.exit !== 0)
      if (refusedStep && refusedStep.from === 'prog') linked.markLine('prog', errorLine(progText, refusedStep.log))
      onCompiled?.({ steps, texts: { prog: progText, ...r.texts } })
      setLive({
        texts: { prog: progText, qppm: r.texts.qppm, qec: r.texts.qec, sysprog: undefined, sysspec: undefined },
        steps, passes: [], vm: null, diff: undefined, qppmSupplied: false,
        liveNote: r.texts.qec
          ? 'The next step, the staged compiler (`.qec` → `.sysprog`), calls FormalRV\'s verified Lean normaliser, which does not run in a browser. Paste or edit a `.sysprog` and check it with the VM.'
          : undefined,
      })
      setDraft(d => ({ ...d, sysprog: undefined }))
      setPane(r.texts.qec ? 'qec' : 'qppm')
      const refused = steps.findIndex(s => s && s.exit !== 0)
      setDrawer(refused >= 0 ? refused : null)
    } catch (e) {
      setLive({ liveNote: `The browser tools could not run: ${(e as Error).message}` })
    } finally { setBusy(null) }
  }

  const recheck = async () => {
    if (!view) return
    setBusy('Checking with the resource VM in your browser…')
    try {
      const { run, verdict } = await runVm(sysText)
      onVmChecked?.({ sysprog: sysText, verdict })
      const summary = verdict
        ? (verdict.status === 'RESOURCE_VALIDATED'
          ? `RESOURCE_VALIDATED: peak ${verdict.peak_qubits_upper_bound} qubits, runtime ${verdict.runtime_upper_bound_ns} ns`
          : `${verdict.status} ${verdict.code ?? ''}: ${verdict.message ?? ''}`)
        : (run.stderr || run.stdout || 'no output')
      const steps = [...view.steps]
      steps[3] = { from: 'sysprog', to: 'vm', command: 'resource_vm Main.sysprog --json', exit: run.code, ms: run.ms, log: summary, where: 'browser' }
      setLive(l => ({ ...(l ?? {}), texts: { ...(l?.texts ?? {}), sysprog: sysText }, steps,
        vm: verdict ? verdictOf(verdict) : null, liveNote: verdict ? undefined : 'The VM did not return a verdict.' }))
      setDrawer(verdict ? 'vm' : 3)
    } catch (e) {
      setLive(l => ({ ...(l ?? {}), liveNote: `The browser VM could not run: ${(e as Error).message}` }))
    } finally { setBusy(null) }
  }

  useEffect(() => {
    if (!base) return
    if (variant?.kind === 'wrong-artifact') setPane('diff')
    else if (variant && !base.texts.sysprog) setPane('sysspec')
    else setPane(p => (p === 'diff' ? (initialPane === 'diff' ? 'qppm' : initialPane) : p))
    const refused = base.steps.findIndex(s => s && s.exit !== 0)
    setDrawer(refused >= 0 ? refused : null)
  }, [base, variant, initialPane])

  const chooseVariant = (id: string | null) => {
    resetLive()
    setLocalVariant(id)
    onVariantChange?.(id)
  }

  if (error) return <div className="m-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div>
  if (!ex || !view) return <div className="flex-1 flex items-center justify-center text-sm text-qcloud-muted">Loading…</div>

  const paneText = (p: PaneId): string | null => {
    switch (p) {
      case 'passes': return view.passes[passIdx]?.text ?? null
      case 'machine': return view.machine || null
      case 'diff': return view.diff ?? null
      case 'sysprog': return view.texts.sysprog != null || draft.sysprog != null ? sysText : null
      default: return view.texts[p] ?? null
    }
  }
  const params = declaredParams(progText)
  const edited = live != null || draft.prog != null || draft.sysprog != null
  const text = paneText(pane)
  const tabs: PaneId[] = [...MAIN_PANES, ...(view.diff ? ['diff' as PaneId] : [])]
  const more = MORE_PANES.filter(p => paneText(p) != null || p === pane)
  const label = (p: PaneId) => (p === 'sysspec' && ex.derive_contracts ? 'Point' : PANE_LABEL[p])
  const runs = showVariants && !playground && ex.variants.length > 0
  const drawerStep = typeof drawer === 'number' ? view.steps[drawer] : null
  const vmState = view.vm ? (view.vm.status === 'RESOURCE_VALIDATED' ? 'ok' : 'fail') : 'none'

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white border border-qcloud-border rounded-lg overflow-hidden">
      <style>{HL_STYLE}</style>

      <div className="flex-1 min-h-0 grid grid-rows-2 lg:grid-rows-1 lg:grid-cols-2">
        {/* source */}
        <section className="min-h-0 flex flex-col border-b lg:border-b-0 lg:border-r border-qcloud-border">
          <div className={bar}>
            {toolbar ?? <span className="text-xs font-mono text-qcloud-text px-1">{ex.unit}.prog</span>}
            {params.map(p => (
              <label key={p} className="inline-flex items-center gap-1 text-xs text-qcloud-muted">
                <span className="font-mono">{p}</span>
                <input type="number" min={0} max={1024} value={scalars[p] ?? 1} title={`compile at ${p} = …`}
                  onChange={e => setScalars(s => ({ ...s, [p]: Math.max(0, Math.min(1024, Number(e.target.value) || 0)) }))}
                  className="w-12 h-7 px-1.5 rounded-md border border-qcloud-border bg-white font-mono text-xs text-qcloud-text" />
              </label>
            ))}
            <span className="flex-1" />
            {edited && !playground && (
              <button onClick={resetLive} title="Back to the recorded run" className={`${btn} text-qcloud-muted hover:text-qcloud-text`}>↺ Reset</button>
            )}
            {!hideCompile && tools && (
              <button onClick={compile} disabled={!!busy} className={`${btn} bg-qcloud-primary text-white hover:opacity-90`}>▶ Compile</button>
            )}
          </div>
          <CodePane value={progText} onMount={linked.register('prog')} readOnly={false}
            onChange={v => { setDraft(d => ({ ...d, prog: v })); onSourceChange?.(v) }} />
        </section>

        {/* output */}
        <section className="min-h-0 flex flex-col">
          <div className={bar}>
            {tabs.map(p => (
              <button key={p} onClick={() => setPane(p)}
                className={`${btn} font-mono ${pane === p ? 'bg-white border border-qcloud-border text-qcloud-primary shadow-sm' : 'text-qcloud-muted hover:text-qcloud-text'}`}>
                {label(p)}
              </button>
            ))}
            {more.length > 0 && (
              <select value={MORE_PANES.includes(pane) ? pane : ''} onChange={e => e.target.value && setPane(e.target.value as PaneId)}
                className={`${sel} ${MORE_PANES.includes(pane) ? 'text-qcloud-primary' : 'text-qcloud-muted'}`} aria-label="More outputs">
                <option value="">More…</option>
                {more.map(p => <option key={p} value={p}>{label(p)}</option>)}
              </select>
            )}
            {pane === 'passes' && view.passes.length > 0 && (
              <select value={passIdx} onChange={e => setPassIdx(Number(e.target.value))} className={`${sel} font-mono max-w-[12rem]`} aria-label="Pass">
                {view.passes.map((p, i) => <option key={p.name} value={i}>{p.name.replace(/^\d+_/, '').replace(/__/g, ' ').replace(/_$/, '')}</option>)}
              </select>
            )}
            <span className="flex-1" />
            {runs && (
              <select value={localVariant ?? ''} onChange={e => chooseVariant(e.target.value || null)} className={`${sel} max-w-[14rem]`}
                aria-label="Recorded run" title={variant?.summary}>
                <option value="">Run: {Object.entries(ex.specialize).map(([k, v]) => `${k}=${v}`).join(', ') || 'recorded'}</option>
                {ex.variants.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
              </select>
            )}
            {pane === 'sysprog' && tools && sysText && (
              <button onClick={recheck} disabled={!!busy} className={`${btn} border border-qcloud-primary text-qcloud-primary bg-white hover:bg-qcloud-primary/5`}>✓ Check</button>
            )}
          </div>
          {text != null
            ? <CodePane key={`${pane}-${passIdx}-${localVariant}`} value={text}
                language={pane === 'diff' ? 'udiff' : 'qparam'} onMount={linked.register('right')}
                readOnly={pane !== 'sysprog'} onChange={v => setDraft(d => ({ ...d, sysprog: v }))} />
            : <div className="flex-1 flex items-center justify-center text-sm text-qcloud-muted px-6 text-center">
                {playground && !live
                  ? (pane === 'sysprog' ? 'Paste a .sysprog listing here to check it with the VM.' : '▶ Compile to see the output.')
                  : pane === 'sysprog'
                    ? (live ? 'No schedule yet: the staged compiler does not run in the browser.' : 'No schedule: the staged compiler refused this run.')
                    : 'Not produced in this run.'}
              </div>}
        </section>
      </div>

      {drawer != null && (
        <div className="shrink-0 max-h-[38%] min-h-[6rem] flex flex-col border-t border-qcloud-border">
          <div className="h-7 shrink-0 px-3 flex items-center justify-between bg-slate-50 border-b border-slate-100">
            <code className="text-[11.5px] text-qcloud-text truncate">
              {drawer === 'vm' ? 'Resource VM' : drawerStep ? `$ ${drawerStep.command}` : ''}
            </code>
            <button onClick={() => setDrawer(null)} aria-label="Close" className="text-qcloud-muted hover:text-qcloud-text px-1">×</button>
          </div>
          <div className="flex-1 min-h-0 overflow-auto px-3 py-2">
            {drawer === 'vm'
              ? <VerdictPanel view={view} />
              : <pre className="text-[11.5px] leading-relaxed whitespace-pre-wrap text-qcloud-text">{drawerStep ? decodeOcamlEscapes(drawerStep.log) || '(no output)' : ''}</pre>}
          </div>
        </div>
      )}

      {/* status: one line, each step opens its log */}
      <div className="h-8 shrink-0 px-2 flex items-center gap-1 border-t border-qcloud-border bg-slate-50 text-[11.5px] overflow-x-auto whitespace-nowrap">
        {STAGE_TOOLS.slice(0, 3).map((t, i) => {
          const st = view.steps[i]
          const failed = st && st.exit !== 0
          return (
            <span key={t.tool} className="contents">
              {i > 0 && <span className="text-slate-300">›</span>}
              <button disabled={!st} onClick={() => setDrawer(drawer === i ? null : i)} title={st ? 'Show the command and its output' : 'not run'}
                className={`px-1.5 py-0.5 rounded font-mono ${drawer === i ? 'bg-white shadow-sm' : 'hover:bg-white'} ${
                  !st ? 'text-slate-400' : failed ? 'text-rose-700' : 'text-qcloud-text'}`}>
                {st ? (failed ? '✗' : '✓') : '·'} {t.tool}{st && !failed ? <span className="text-qcloud-muted"> {st.ms} ms</span> : failed ? ' refused' : ''}
              </button>
            </span>
          )
        })}
        <span className="text-slate-300">›</span>
        <button onClick={() => setDrawer(drawer === 'vm' ? null : 'vm')}
          className={`px-1.5 py-0.5 rounded ${drawer === 'vm' ? 'bg-white shadow-sm' : 'hover:bg-white'} ${
            vmState === 'ok' ? 'text-emerald-700 font-semibold' : vmState === 'fail' ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>
          {vmState === 'ok'
            ? `✓ VM: ${view.vm!.peak_qubits} qubits · ${view.vm!.runtime_ns} ns`
            : vmState === 'fail' ? `✗ VM: ${view.vm!.code ?? view.vm!.status}` : '· VM'}
        </button>
        <span className="flex-1" />
        <span className="text-qcloud-muted pl-3">
          {busy ?? (live ? 'ran in your browser' : playground ? '' : `recorded run · FormalRV ${ex.formalrv_commit.slice(0, 8)}`)}
        </span>
      </div>
    </div>
  )
}
